import { useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { useDeliveryNote, useDeleteDeliveryNote } from "@/api/hooks";
import { PageBody } from "@/components/ui/PageHeader";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Panel } from "@/components/ui/Panel";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { DeliveryDocument } from "@/components/logistics/DeliveryDocument";
import { DeliverySourcesPanel } from "@/components/logistics/DeliverySourcesPanel";
import {
  buildDeliveryNoteHtml,
  printDeliveryNote,
} from "@/lib/printDeliveryNote";
import { exportDeliveryCsv } from "@/lib/deliveryCsv";
import { getLocalizedError } from "@/lib/api";
import { translateStatus } from "@/domain/i18n";
import { formatDate, formatDateTime } from "@/domain/datetime";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";
import { DocumentExportDialog } from "@/components/domain/DocumentExportDialog";
export function DeliveryNoteDetailPage() {
  const { id } = useParams();
  const { search: context } = useLocation();
  const archived = new URLSearchParams(context).get("archived") === "true";
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const result = useDeliveryNote(id, archived);
  const dn = result.data;
  const remove = useDeleteDeliveryNote();
  const [confirm, setConfirm] = useState(false);
  const [preview, setPreview] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lock = useRef(false);
  usePageCrumbs(dn ? [{ label: dn.dnNo }] : result.error ? [{ label: t("common.unavailable") }] : [], `/delivery-notes${context}`);
  if (result.isLoading)
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  if (result.error || !dn)
    return (
      <PageBody>
        <ErrorState
          message={t("deliveryNotes.errorLoad")}
          onRetry={() => void result.refetch()}
        />
      </PageBody>
    );
  const canUpdate =
    Boolean(user && can("records:update")) &&
    !dn.deletedAt;
  const canCreate = Boolean(user && can("records:create"));
  const canDelete =
    Boolean(user && can("records:delete")) &&
    !dn.deletedAt;
  const edit = (action?: string) =>
    navigate(
      `/delivery-notes/${dn.id}/edit${action ? `?action=${action}` : context}`,
    );
  return (
    <PageBody>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="font-mono text-xl font-semibold">{dn.dnNo}</h1>
          <Badge
            tone={
              dn.deliveryStatus === "Delivered"
                ? "ok"
                : dn.deliveryStatus === "In Transit"
                  ? "info"
                  : dn.deliveryStatus === "Cancelled"
                    ? "danger"
                    : "neutral"
            }
          >
            {translateStatus(t, dn.deliveryStatus)}
          </Badge>
          {dn.deletedAt && <Badge>{t("invoiceWorkspace.archived")}</Badge>}
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setPreview(true)}
          >
            {t("deliveryWorkspace.preview")}
          </Button>
          <Button
            size="sm"
            variant="secondary"
            aria-haspopup="dialog"
            onClick={() => setExportOpen(true)}
          >
            {t("common.export")}
          </Button>
          {canUpdate &&
            (dn.deliveryStatus === "Pending" ||
              dn.deliveryStatus === "In Transit") && (
              <Button size="sm" variant="secondary" onClick={() => edit()}>
                {t("common.edit")}
              </Button>
            )}
          {canUpdate && dn.deliveryStatus === "Pending" && (
            <Button size="sm" onClick={() => edit("dispatch")}>
              {t("deliveryWorkspace.dispatch")}
            </Button>
          )}
          {canUpdate && dn.deliveryStatus === "In Transit" && (
            <Button size="sm" onClick={() => edit("complete")}>
              {t("deliveryWorkspace.confirmReceipt")}
            </Button>
          )}
          {canUpdate &&
            (dn.deliveryStatus === "Pending" ||
              dn.deliveryStatus === "In Transit") && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => edit("cancel")}
              >
                {t("deliveryWorkspace.cancelShipment")}
              </Button>
            )}
          {canUpdate && dn.deliveryStatus === "Delivered" && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => edit("correct")}
            >
              {t("deliveryWorkspace.correct")}
            </Button>
          )}
          {canDelete && (
            <Button
              size="sm"
              variant="danger"
              disabled={dn.deliveryStatus === "In Transit" || remove.isPending}
              onClick={() => setConfirm(true)}
            >
              {t("deliveryWorkspace.archive")}
            </Button>
          )}
        </div>
      </div>
      <div className="my-3 flex flex-wrap items-center gap-4 text-sm">
        {dn.sourceId && dn.refType && dn.sourceAvailable !== false && (
          <Link
            className="font-mono text-brand underline"
            to={`/${dn.refType === "packing" ? "packing-lists" : "invoices"}/${dn.sourceId}`}
          >
            {dn.refType === "packing" ? dn.plNo : dn.invoiceNo}
          </Link>
        )}
        {dn.sourcePiId && dn.sourcePiAvailable !== false && (
          <Link
            className="font-mono text-brand underline"
            to={`/proforma-invoices/${dn.sourcePiId}`}
          >
            {t("common.fromPi")} {dn.piNo}
          </Link>
        )}
        {canCreate &&
          dn.sourceId &&
          dn.refType &&
          dn.sourceAvailable !== false && (
            <Link
              className="text-brand underline"
              to={`/delivery-notes/new?refType=${dn.refType}&sourceId=${dn.sourceId}`}
            >
              {t("deliveryWorkspace.nextShipment")}
            </Link>
          )}
      </div>
      {dn.sourceAvailable === false && (
        <p className="mb-3 text-xs text-ink-soft">
          {t("deliveryWorkspace.sourceArchived")} ·{" "}
          {dn.refType === "packing" ? dn.plNo : dn.invoiceNo} · {dn.piNo}
        </p>
      )}
      <p className="mb-3 text-xs text-ink-soft">
        {t(
          dn.deliveryStatus === "Delivered"
            ? "deliveryWorkspace.completedHint"
            : dn.deliveryStatus === "Cancelled"
              ? "deliveryWorkspace.cancelledHint"
              : dn.deletedAt
                ? "deliveryWorkspace.archivedPlanHint"
                : "deliveryWorkspace.planningHint",
        )}
      </p>
      {error && (
        <p role="alert" className="my-3 text-sm text-danger">
          {error}
        </p>
      )}
      <DeliveryDocument dn={dn} />
      <DeliverySourcesPanel dn={dn} />
      <Panel className="mt-3 p-4">
        <h2 className="mb-3 font-medium">{t("deliveryWorkspace.history")}</h2>
        {!dn.events?.length && (
          <p className="text-sm text-ink-faint">
            {t("deliveryWorkspace.legacyHistory")}
          </p>
        )}
        <ol className="space-y-3">
          {dn.events?.map((e) => (
            <li key={e.id} className="border-b border-line pb-3 last:border-0">
              <p className="text-sm">
                <span className="font-medium">
                  {t(`deliveryWorkspace.event_${e.kind}`)}
                </span>
                {e.fromStatus && ` · ${translateStatus(t, e.fromStatus)}`}
                {e.toStatus && ` → ${translateStatus(t, e.toStatus)}`}
              </p>
              <p className="mt-1 text-xs text-ink-faint">
                {e.actorName || "—"} ·{" "}
                {formatDateTime(e.createdAt, i18n.language)}
              </p>
              {e.reason && (
                <p className="mt-1 whitespace-pre-line text-sm">{e.reason}</p>
              )}
              {e.changes?.map((c, index) => (
                <p
                  key={index}
                  className="mt-1 whitespace-pre-line text-xs text-ink-soft"
                >
                  {c.lineNo
                    ? `${t("deliveryWorkspace.line")} ${c.lineNo} · `
                    : ""}
                  {t(`deliveryWorkspace.field_${c.field}`)}:{" "}
                  {c.field === "deliveryDate"
                    ? formatDate(typeof c.before === "number" ? c.before : null)
                    : String(c.before ?? "—")}{" "}
                  →{" "}
                  {c.field === "deliveryDate"
                    ? formatDate(typeof c.after === "number" ? c.after : null)
                    : String(c.after ?? "—")}
                </p>
              ))}
            </li>
          ))}
        </ol>
      </Panel>
      <ConfirmDialog
        open={confirm}
        title={t("deliveryWorkspace.archive")}
        description={`${dn.dnNo}. ${t("deliveryWorkspace.archiveHint")}`}
        confirmLabel={t("deliveryWorkspace.archive")}
        cancelLabel={t("common.cancel")}
        busy={remove.isPending}
        onCancel={() => setConfirm(false)}
        onConfirm={async () => {
          if (lock.current) return;
          lock.current = true;
          try {
            await remove.mutateAsync(dn.id);
            navigate(`/delivery-notes${context}`);
          } catch (err) {
            setError(getLocalizedError(err, t, "deliveryNotes.errorSave"));
            setConfirm(false);
          } finally {
            lock.current = false;
          }
        }}
      />
      {exportOpen && (
        <DocumentExportDialog
          key={dn.id}
          documentNo={dn.dnNo}
          onCsv={() => exportDeliveryCsv(dn, t)}
          onPdf={() => { setExportOpen(false); setPreview(true); }}
          onExcel={async () => {
            const { exportDeliveryNoteExcel } = await import("@/lib/exportDeliveryNoteExcel");
            return exportDeliveryNoteExcel(dn);
          }}
          onClose={() => setExportOpen(false)}
        />
      )}
      {preview && (
        <Modal
          className="max-w-6xl"
          title={`${t("deliveryWorkspace.preview")} · ${dn.dnNo}`}
          closeLabel={t("common.close")}
          onClose={() => setPreview(false)}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 p-3">
            <SaveAsPdfHint />
            <Button onClick={() => printDeliveryNote(dn)}>
              {t("common.print")}
            </Button>
          </div>
          <iframe
            title={t("deliveryWorkspace.preview")}
            sandbox="allow-same-origin"
            srcDoc={buildDeliveryNoteHtml(dn)}
            className="min-h-[70dvh] w-full bg-surface"
          />
        </Modal>
      )}
    </PageBody>
  );
}
